import React, { useEffect, useRef } from "react";
import {
  CONTENT_TYPE_TEXT_LEFT,
  CONTENT_TYPE_TEXT_RIGHT,
  CONTENT_TYPE_TEXT_CENTER,
  CONTENT_TYPE_TEXT_IMAGE_LEFT,
  CONTENT_TYPE_TEXT_IMAGE_RIGHT,
  CONTENT_TYPE_IMAGE_LEFT,
  CONTENT_TYPE_IMAGE_RIGHT,
  CONTENT_TYPE_IMAGE_CENTER,
  CONTENT_TYPE_IMAGE_FULL
} from "../contexts/content-sections";

const useOnScreen = (ref, setActiveHeader, id) => {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting === true) setActiveHeader(id);
      },
      { rootMargin: "0px 0px -300px 0px" }
    );
    observer.observe(ref.current);
    return () => {
      observer.disconnect();
    };
  }, [ref, setActiveHeader, id]);
};

const PageH1 = ({ title, colorSectionHeaderText, id, setActiveHeader }) => {
  const ref = useRef();
  useOnScreen(ref, setActiveHeader, id);
  return (
    <h1
      className="text-lg md:text-2xl font-semibold"
      style={{ color: colorSectionHeaderText }}
      ref={ref}
    >
      {title}
    </h1>
  );
};

const PageH2 = ({ title, colorSectionHeaderText, id, setActiveHeader }) => {
  const ref = useRef();
  useOnScreen(ref, setActiveHeader, id);
  return (
    <h2
      className="text-lg md:text-2xl font-semibold"
      style={{ color: colorSectionHeaderText }}
      ref={ref}
    >
      {title}
    </h2>
  );
};

const PageH3 = ({ title, colorSectionHeaderText, id, setActiveHeader }) => {
  const ref = useRef();
  useOnScreen(ref, setActiveHeader, id);

  return (
    <h3
      className="text-lg md:text-2xl font-semibold"
      style={{ color: colorSectionHeaderText }}
      ref={ref}
    >
      {title}
    </h3>
  );
};

export const TextLeft = ({ content, colorSectionText }) => (
  <div
    className="leading-loose text-base xl:text-lg xl:leading-9 prose"
    style={{ color: colorSectionText }}
    dangerouslySetInnerHTML={{ __html: content }}
  />
);

const TextRight = ({ content, colorSectionText }) => (
  <div
    className="text-right leading-loose text-base xl:text-lg xl:leading-9 prose"
    style={{ color: colorSectionText }}
    dangerouslySetInnerHTML={{ __html: content }}
  />
);

const TextCenter = ({ content, colorSectionText }) => (
  <div
    className="text-center leading-loose text-base xl:text-lg xl:leading-9 prose"
    style={{ color: colorSectionText }}
    dangerouslySetInnerHTML={{ __html: content }}
  />
);

const ImageOnLeft = ({ content, colorSectionText, imageUrl }) => (
  <div className="columns-1 md:columns-2">
    <div>
      <img src={imageUrl} />
    </div>
    <div
      className="leading-loose text-base xl:text-lg xl:leading-9 prose"
      style={{ color: colorSectionText }}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  </div>
);

const ImageOnRight = ({ content, colorSectionText, imageUrl }) => (
  <div className="columns-1 md:columns-2">
    <div
      className="leading-loose text-base xl:text-lg xl:leading-9 prose"
      style={{ color: colorSectionText }}
      dangerouslySetInnerHTML={{ __html: content }}
    />
    <div>
      <img src={imageUrl} />
    </div>
  </div>
);

const ImageCenter = ({ imageUrl, height, width }) => (
  <div className="flex justify-center">
    <img src={imageUrl} height={height} width={width} />
  </div>
);

const ImageCenterFull = ({ imageUrl }) => (
  <div>
    <img className="object-fill" src={imageUrl} />
  </div>
);

const ImageLeft = ({ imageUrl, height, width }) => (
  <div className="flex justify-start">
    <img src={imageUrl} height={height} width={width} />
  </div>
);

const ImageRight = ({ imageUrl, height, width }) => (
  <div className="flex justify-end">
    <img src={imageUrl} height={height} width={width} />
  </div>
);

const ContentSections = ({
  sections,
  colorSectionText,
  colorSectionHeaderText,
  setActiveHeader
}) =>
  sections.map(
    ({
      content,
      content_type: contentType,
      heading,
      heading_level: headingLevel,
      id,
      image,
      show_heading: showHeading
    }) => {
      const imageUrl = image && "url" in image ? image["url"] : null;
      const imageHeight = image && "height" in image ? image["height"] : null;
      const imageWidth = image && "width" in image ? image["width"] : null;

      const headingOut = (
        <>
          {showHeading == true && headingLevel === 1 && (
            <PageH1
              title={heading}
              colorSectionHeaderText={colorSectionHeaderText}
              setActiveHeader={setActiveHeader}
              id={id}
            />
          )}
          {showHeading == true && headingLevel === 2 && (
            <PageH2
              title={heading}
              colorSectionHeaderText={colorSectionHeaderText}
              setActiveHeader={setActiveHeader}
              id={id}
            />
          )}
          {showHeading == true &&
            (headingLevel === 3 ||
              headingLevel === 4 ||
              headingLevel === 5 ||
              headingLevel === 6) && (
              <PageH3
                title={heading}
                colorSectionHeaderText={colorSectionHeaderText}
                setActiveHeader={setActiveHeader}
                id={id}
              />
            )}
        </>
      );

      let sectionOut;
      switch (contentType) {
        case CONTENT_TYPE_TEXT_LEFT:
          sectionOut = (
            <TextLeft content={content} colorSectionText={colorSectionText} />
          );
          break;
        case CONTENT_TYPE_TEXT_RIGHT:
          sectionOut = (
            <TextRight content={content} colorSectionText={colorSectionText} />
          );
          break;
        case CONTENT_TYPE_TEXT_CENTER:
          sectionOut = (
            <TextCenter content={content} colorSectionText={colorSectionText} />
          );
          break;
        case CONTENT_TYPE_TEXT_IMAGE_LEFT:
          sectionOut = (
            <ImageOnLeft
              content={content}
              colorSectionText={colorSectionText}
              imageUrl={imageUrl}
            />
          );
          break;
        case CONTENT_TYPE_TEXT_IMAGE_RIGHT:
          sectionOut = (
            <ImageOnRight
              content={content}
              colorSectionText={colorSectionText}
              imageUrl={imageUrl}
            />
          );
          break;
        case CONTENT_TYPE_IMAGE_LEFT:
          sectionOut = (
            <ImageLeft
              imageUrl={imageUrl}
              height={imageHeight}
              width={imageWidth}
            />
          );
          break;
        case CONTENT_TYPE_IMAGE_RIGHT:
          sectionOut = (
            <ImageRight
              imageUrl={imageUrl}
              height={imageHeight}
              width={imageWidth}
            />
          );
          break;
        case CONTENT_TYPE_IMAGE_CENTER:
          sectionOut = (
            <ImageCenter
              imageUrl={imageUrl}
              height={imageHeight}
              width={imageWidth}
            />
          );
          break;
        case CONTENT_TYPE_IMAGE_FULL:
          sectionOut = <ImageCenterFull imageUrl={imageUrl} />;
          break;
        default:
          sectionOut = null;
          break;
      }

      return (
        <section key={id} id={id}>
          {headingOut}
          {sectionOut}
        </section>
      );
    }
  );

export default ContentSections;
